// Thin axios wrapper that talks to the gateway's /api/tools endpoint.
// Every method maps to a tool name + JSON args; the HTTP layer returns
// the tool's result verbatim (including `{ error: 'entitlement_exceeded' }`
// shapes — the caller pattern-matches on `error` to render the right UI).
//
// Auth: a header derived from the Pinia auth store is added on every
// request. In dev (permissive mode) it's a tenant_id hint; in prod
// (Sprint 8+) it's a JWT from Entra External ID.

import axios, { AxiosError, type AxiosInstance } from 'axios';
import { ref } from 'vue';
import { useAuthStore } from '../stores/auth';

const baseURL = import.meta.env.VITE_API_BASE ?? '';   // empty → same origin (vite proxy in dev)

/** ADR 42 — the correlation id (x-request-id) of the most recent API response.
 *  Lets a view link "inspect the request it just made". */
export const lastRequestId = ref<string | null>(null);

export async function downloadScadaInstaller(project: string, build: string, expectedHash: string): Promise<Blob> {
  const response = await getClient().get<ArrayBuffer>(`/api/projects/${encodeURIComponent(project)}/scada-installers/${encodeURIComponent(build)}`, {responseType: 'arraybuffer'});
  const digest = await crypto.subtle.digest('SHA-256', response.data);
  const hash = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  if (hash !== expectedHash) throw Error('Installer checksum mismatch');
  const contentType = response.headers['content-type'];
  return new Blob([response.data], {type: typeof contentType === 'string' ? contentType : 'application/octet-stream'});
}

export interface ToolResult<T = unknown> {
  /** Set when the gateway returned an entitlement-exceeded structured payload. */
  error?: 'entitlement_exceeded';
  gate?: string;
  current?: number | boolean;
  limit?: number | boolean;
  suggestion?: string;
  /** Tool-specific fields when error is absent. */
  data?: T;
}

let client: AxiosInstance | null = null;

function getClient(): AxiosInstance {
  if (client) return client;
  client = axios.create({
    baseURL,
    // chat_completion runs an LLM loop with up to 8 turns; each turn is
    // an Anthropic round-trip plus tool execution. Three turns ≈ 30-45s.
    // SSE streaming (Sprint 9) will make this less of a hard ceiling.
    timeout: 120_000,
    headers: { 'Content-Type': 'application/json' },
  });
  client.interceptors.request.use(async (cfg) => {
    const auth = useAuthStore();
    // In OIDC mode, opportunistically refresh the token if it's near
    // expiry. Cheap when not needed (a single Date.now() comparison).
    await auth.refreshIfNeeded();

    const headers = cfg.headers ?? {};
    if (auth.bearer) {
      (headers as Record<string, string>).Authorization = `Bearer ${auth.bearer}`;
    }
    // tenantId hint is harmless in OIDC mode (the gateway prefers the
    // JWT claim) and is the only signal in permissive mode.
    if (auth.tenantId) {
      (headers as Record<string, string>)['x-lymon-tenant-id'] = auth.tenantId;
    }
    // Sprint 10 — user_sub identity for permissive mode (no OIDC). Lets
    // the dev exercise multi-user features (invitation accept, favorites,
    // members). The gateway uses this only when verify() didn't set a
    // sub via JWT.
    if (auth.mode === 'permissive' && auth.manualUserSub) {
      (headers as Record<string, string>)['x-lymon-user-sub'] = auth.manualUserSub;
    }
    cfg.headers = headers;
    return cfg;
  });

  // 401 from the gateway in OIDC mode usually means the token expired
  // between our refresh check and the request reaching the gateway.
  // One retry with a forced refresh covers the boundary case; further
  // 401s bubble up to the caller.
  client.interceptors.response.use(
    (r) => {
      // ADR 42 — surface the correlation id the gateway echoes, so a view can
      // deep-link into inspect_request for the request it just made.
      const rid = r.headers?.['x-request-id'];
      if (typeof rid === 'string' && rid) lastRequestId.value = rid;
      return r;
    },
    async (err: AxiosError) => {
      const auth = useAuthStore();
      if(err.response?.status===401 && auth.mode==='operator') await auth.logout();
      const orig = err.config as (typeof err.config & { __retried?: boolean }) | undefined;
      if (
        err.response?.status === 401 &&
        auth.mode === 'oidc' &&
        orig &&
        !orig.__retried
      ) {
        orig.__retried = true;
        await auth.refreshIfNeeded();
        if (auth.bearer && orig.headers) {
          (orig.headers as Record<string, string>).Authorization = `Bearer ${auth.bearer}`;
        }
        return client!.request(orig);
      }
      return Promise.reject(err);
    },
  );
  return client;
}

// ---------------------------------------------------------------------
// Chat streaming — POST + SSE response body parsed via fetch streaming.
// EventSource would be simpler but it's GET-only, and the chat payload
// (messages + tools) exceeds query string budgets in practice.
// ---------------------------------------------------------------------

export type ChatStreamEvent =
  | { kind: 'text_delta'; turn: number; text: string }
  | {
      kind: 'tool_call';
      turn: number;
      tool_call_id: string;
      name: string;
      input: Record<string, unknown>;
    }
  | {
      kind: 'tool_result';
      turn: number;
      tool_call_id: string;
      content: string;
      is_error?: boolean;
    }
  | { kind: 'turn_end'; turn: number; stop_reason: string }
  | {
      // Chat actions: the assistant proposed a state-changing tool; the UI must
      // show a confirm card before it runs (executed via confirmChatTool).
      kind: 'confirm_request';
      turn: number;
      tool_call_id: string;
      name: string;
      input: Record<string, unknown>;
    }
  | {
      // Confirm-and-continue: the outcome of a user-approved mutating tool,
      // emitted before the model's acknowledgment streams.
      kind: 'confirm_result';
      name: string;
      ok: boolean;
      result?: unknown;
      error?: string;
      detail?: string;
    }
  | {
      // ADR 33 — MCP Apps: a tool result with an interactive UI to render inline.
      kind: 'ui_resource';
      turn: number;
      tool_call_id: string;
      resourceUri?: string;
      mimeType?: string;
      url?: string;
      html?: string;
    }
  | {
      kind: 'done';
      turns: number;
      hit_turn_cap: boolean;
      final_text: string;
      usage: { input_tokens: number; output_tokens: number };
    }
  | { kind: 'error'; message: string; retryable: boolean };

export interface ChatStreamRequest {
  messages: Array<{ role: 'user' | 'assistant' | 'system'; content: unknown }>;
  system?: string;
  tools?: string[];
  max_tokens?: number;
  temperature?: number;
  /** Confirm-and-continue: run this user-approved tool before the turn, then
   *  let the model acknowledge its result (chat-actions flow). */
  confirm?: { name: string; arguments?: Record<string, unknown> };
  /** Per-session override for the confirm gate: true = run actions without a
   *  confirm card, false = always confirm. Omit → the tenant default. */
  auto_run_tools?: boolean;
}

/**
 * Open a POST /api/chat/completion/stream connection and dispatch the
 * SSE events to onEvent until the stream ends or signal aborts. Resolves
 * when the server closes the stream (after a `done` or `error` event).
 *
 * The caller owns onEvent's side effects — typically pushing the deltas
 * into a Vue ref-backed message buffer so the UI updates as text flows.
 */
export async function streamChatCompletion(
  body: ChatStreamRequest,
  onEvent: (ev: ChatStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const auth = useAuthStore();
  await auth.refreshIfNeeded();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth.bearer) headers.Authorization = `Bearer ${auth.bearer}`;
  if (auth.tenantId) headers['x-lymon-tenant-id'] = auth.tenantId;
  if (auth.mode === 'permissive' && auth.manualUserSub) {
    headers['x-lymon-user-sub'] = auth.manualUserSub;
  }

  const resp = await fetch(`${baseURL}/api/chat/completion/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => '');
    throw new Error(`stream open failed: ${resp.status} ${text}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE events are separated by blank lines. Each event is one or
    // more `field: value\n` lines. We only care about `event:` and
    // `data:` because the gateway emits exactly those.
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';
    for (const raw of chunks) {
      const ev = parseSseChunk(raw);
      if (ev) onEvent(ev);
    }
  }
}

function parseSseChunk(raw: string): ChatStreamEvent | null {
  let event = '';
  let dataStr = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event: ')) event = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataStr = line.slice(6);
  }
  if (!event || !dataStr) return null;
  try {
    const data = JSON.parse(dataStr) as Record<string, unknown>;
    // The backend's onEvent payload already includes the kind in `kind`,
    // but the SSE event name is the same value with dashes → underscores.
    // We override kind from the event name for safety against drift.
    return { ...data, kind: event } as ChatStreamEvent;
  } catch {
    return null;
  }
}

export interface ToolDescriptor {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
  gates: Array<Record<string, unknown>>;
}

export async function listTools(): Promise<ToolDescriptor[]> {
  const r = await getClient().get<{ tools: ToolDescriptor[] }>('/api/tools');
  return r.data.tools;
}

/**
 * Run an MCP tool by name. Returns the parsed body. On non-2xx, throws
 * with the structured error attached as .gatewayError for the caller
 * to display.
 */
/**
 * ADR 33 — proxy a tool call made by an interactive app (the dock's AppBridge
 * forwards the app's `tools/call` here). The gateway runs it with tenant + audit
 * + entitlement guarantees and gates mutations behind `confirmed`.
 */
export async function uiToolCall(
  name: string,
  args: Record<string, unknown> = {},
  confirmed = false,
): Promise<{ ok?: boolean; result?: unknown; needs_confirmation?: boolean; tool?: string; error?: string; detail?: string }> {
  try {
    const r = await getClient().post('/api/ui/tools/call', { name, arguments: args, confirmed });
    return r.data as { ok?: boolean; result?: unknown; needs_confirmation?: boolean };
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      const body = err.response.data as { error?: string; detail?: string };
      return { error: body?.error ?? 'tool_error', detail: body?.detail };
    }
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Run a state-changing tool the assistant proposed, after the user confirmed it
 * in the chat (POST /api/chat/tools/confirm). Tenant + audit + entitlements are
 * enforced server-side; source is recorded as 'chat'.
 */
export async function confirmChatTool(
  name: string,
  args: Record<string, unknown> = {},
): Promise<{ ok?: boolean; result?: unknown; error?: string; detail?: string }> {
  try {
    const r = await getClient().post('/api/chat/tools/confirm', { name, arguments: args });
    return r.data as { ok?: boolean; result?: unknown };
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      const body = err.response.data as { error?: string; detail?: string };
      return { error: body?.error ?? 'tool_error', detail: body?.detail };
    }
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Upload an asset (image/SVG) as RAW BYTES to POST /api/asset-upload — no
 * base64 inflation, up to 25 MB. Same auth interceptors as callTool. `mime`
 * overrides file.type (browsers often report an empty type for .svg). Returns
 * the same shape as the asset_create tool ({ ok, asset_id, url, … }).
 */
export async function uploadAsset(
  file: File,
  mime: string,
  projectId?: string | null,
): Promise<{ ok?: boolean; asset_id?: string; url?: string; kind?: string; size?: number; error?: string; detail?: string }> {
  try {
    const r = await getClient().post('/api/asset-upload', file, {
      headers: { 'Content-Type': mime || 'application/octet-stream', 'X-Asset-Name': encodeURIComponent(file.name), ...(projectId ? { 'X-Project-Id': projectId } : {}) },
    });
    return r.data;
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      const body = err.response.data as { error?: string; detail?: string };
      return { error: body?.error ?? 'upload_error', detail: body?.detail };
    }
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export async function callTool<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  try {
    const r = await getClient().post<T>(`/api/tools/${name}`, args);
    return r.data;
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      const code = err.response.status;
      const body = err.response.data as { error?: string; detail?: string };
      const e = new Error(body?.detail ?? body?.error ?? err.message) as Error & {
        statusCode: number;
        gatewayError?: string;
      };
      e.statusCode = code;
      e.gatewayError = body?.error;
      throw e;
    }
    throw err;
  }
}

// ---------------------------------------------------------------------
// Persistent chat conversations (general + Designer). Server-backed
// sessions scoped to global / a design / a dashboard, each private or
// shared with the scope's collaborators. See conversationsApi on the
// gateway.
// ---------------------------------------------------------------------

export type ChatScopeKind = 'global' | 'design' | 'dashboard' | 'component';
export type ChatVisibility = 'private' | 'shared';

export interface ChatSessionDto {
  id: string;
  title: string;
  visibility: ChatVisibility;
  scope_kind: ChatScopeKind;
  scope_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ChatAttachmentDto { mediaType: string; dataBase64: string }
export interface ChatMessageDto {
  role: 'user' | 'assistant' | 'system';
  content: unknown;          // string OR an array of trace blocks
  images?: ChatAttachmentDto[];
  author_sub?: string | null;
}

export async function listChatSessions(scopeKind: ChatScopeKind, scopeId?: string | null): Promise<ChatSessionDto[]> {
  const params = new URLSearchParams({ scope_kind: scopeKind });
  if (scopeKind !== 'global' && scopeId) params.set('scope_id', scopeId);
  const r = await getClient().get<{ sessions: ChatSessionDto[] }>(`/api/chat/sessions?${params.toString()}`);
  return r.data.sessions;
}

export async function createChatSession(input: {
  scope_kind: ChatScopeKind; scope_id?: string | null; title?: string; visibility?: ChatVisibility;
}): Promise<ChatSessionDto> {
  const r = await getClient().post<{ session: ChatSessionDto }>('/api/chat/sessions', input);
  return r.data.session;
}

export async function updateChatSession(id: string, patch: { title?: string; visibility?: ChatVisibility }): Promise<ChatSessionDto> {
  const r = await getClient().patch<{ session: ChatSessionDto }>(`/api/chat/sessions/${id}`, patch);
  return r.data.session;
}

export async function deleteChatSession(id: string): Promise<void> {
  await getClient().delete(`/api/chat/sessions/${id}`);
}

export async function getChatMessages(id: string): Promise<{ session: ChatSessionDto; messages: ChatMessageDto[] }> {
  const r = await getClient().get<{ session: ChatSessionDto; messages: ChatMessageDto[] }>(`/api/chat/sessions/${id}/messages`);
  return r.data;
}

export async function appendChatMessage(id: string, msg: ChatMessageDto): Promise<void> {
  await getClient().post(`/api/chat/sessions/${id}/messages`, msg);
}
